import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { trackedSet } from "@ember/reactive/collections";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { INPUT_DELAY } from "discourse/lib/environment";
import { has } from "discourse/truth-helpers";
import DConditionalLoadingSpinner from "discourse/ui-kit/d-conditional-loading-spinner";
import dLoadingSpinner from "discourse/ui-kit/helpers/d-loading-spinner";
import { i18n } from "discourse-i18n";
import EditNavigationMenuModal from "./modal.gjs";

export default class SidebarEditNavigationMenuTagsModal extends Component {
  @service currentUser;
  @service siteSettings;
  @service store;

  @tracked disableFiltering = false;
  @tracked saving = false;
  @tracked selectedTags = trackedSet([...this.currentUser.sidebarTagNames]);
  @tracked tags = [];
  @tracked tagsLoading = false;
  observer;
  onlySelected = false;
  onlyUnselected = false;

  constructor() {
    super(...arguments);
    this.#loadTags();
  }

  @action
  didInsertTag(element) {
    const tagName = element.dataset.tagName;
    const lastTagName = this.tags.content.at(-1).name;

    if (tagName === lastTagName) {
      if (this.observer) {
        this.observer.disconnect();
      } else {
        const root = document.querySelector(".d-modal__body");
        const style = window.getComputedStyle(root);
        const marginTop = parseFloat(style.marginTop);
        const paddingTop = parseFloat(style.paddingTop);

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.tags.loadMore();
              }
            });
          },
          {
            root: document.querySelector(".d-modal__body"),
            rootMargin: `0px 0px ${marginTop + paddingTop}px 0px`,
            threshold: 1.0,
          }
        );
      }

      this.observer.observe(element);
    }
  }

  @action
  resetFilter() {
    this.onlySelected = false;
    this.onlyUnselected = false;
    this.#loadTags();
  }

  @action
  filterSelected() {
    this.onlySelected = true;
    this.onlyUnselected = false;
    this.#loadTags();
  }

  @action
  filterUnselected() {
    this.onlySelected = false;
    this.onlyUnselected = true;
    this.#loadTags();
  }

  @action
  onFilterInput(filter) {
    this.disableFiltering = true;
    discourseDebounce(this, this.#performFiltering, filter, INPUT_DELAY);
  }

  @action
  deselectAll() {
    this.selectedTags.clear();
  }

  @action
  resetToDefaults() {
    this.selectedTags = trackedSet(
      this.siteSettings.default_navigation_menu_tags.split("|")
    );
  }

  @action
  toggleTag(tag) {
    if (this.selectedTags.has(tag)) {
      this.selectedTags.delete(tag);
    } else {
      this.selectedTags.add(tag);
    }
  }

  @action
  async save() {
    this.saving = true;
    const initialSidebarTags = this.currentUser.sidebar_tags;
    this.currentUser.set("sidebar_tag_names", [...this.selectedTags]);

    try {
      const result = await this.currentUser.save(["sidebar_tag_names"]);
      this.currentUser.set("sidebar_tags", result.user.sidebar_tags);
      this.args.closeModal();
    } catch (error) {
      this.currentUser.set("sidebar_tags", initialSidebarTags);
      popupAjaxError(error);
    } finally {
      this.saving = false;
    }
  }

  async #loadTags() {
    try {
      this.tagsLoading = true;

      const findArgs = {};

      if (this.filter) {
        findArgs.filter = this.filter;
      }

      if (this.onlySelected) {
        if (this.selectedTags.size === 0) {
          this.tags = [];
          return;
        }

        findArgs.only_tags = [...this.selectedTags].join(",");
      } else if (this.onlyUnselected) {
        findArgs.exclude_tags = [...this.selectedTags].join(",");
      }

      try {
        const tags = await this.store.findAll("listTag", findArgs);
        this.tags = tags;
      } catch (error) {
        popupAjaxError(error);
      }
    } finally {
      this.tagsLoading = false;
      this.disableFiltering = false;
    }
  }

  #performFiltering(filter) {
    this.filter = filter.toLowerCase();
    this.#loadTags();
  }

  <template>
    <EditNavigationMenuModal
      class="sidebar__edit-navigation-menu__tags-modal"
      @closeModal={{@closeModal}}
      @deselectAll={{this.deselectAll}}
      @deselectAllText={{i18n "sidebar.tags_form_modal.subtitle.text"}}
      @filterSelected={{this.filterSelected}}
      @filterUnselected={{this.filterUnselected}}
      @inputFilterPlaceholder={{i18n
        "sidebar.tags_form_modal.filter_placeholder"
      }}
      @loading={{or this.tagsLoading this.disableFiltering}}
      @onFilterInput={{this.onFilterInput}}
      @resetFilter={{this.resetFilter}}
      @resetToDefaults={{this.resetToDefaults}}
      @save={{this.save}}
      @saving={{this.saving}}
      @showResetDefaultsButton={{gt
        this.siteSettings.default_navigation_menu_tags.length
        0
      }}
      @title="sidebar.tags_form_modal.title"
    >
      {{#if this.tagsLoading}}
        {{dLoadingSpinner size="large"}}
      {{else}}
        <form class="sidebar-tags-form">
          {{#each this.tags.content as |tag|}}
            <div
              class="sidebar-tags-form__tag"
              data-tag-name={{tag.name}}
              {{didInsert this.didInsertTag}}
            >
              <input
                checked={{has this.selectedTags tag.name}}
                class="sidebar-tags-form__input"
                id={{concat "sidebar-tags-form__input--" tag.name}}
                type="checkbox"
                {{on "click" (fn this.toggleTag tag.name)}}
              />

              <label
                class="sidebar-tags-form__tag-label"
                for={{concat "sidebar-tags-form__input--" tag.name}}
              >
                <p>
                  <span class="sidebar-tags-form__tag-label-name">
                    {{tag.name}}
                  </span>

                  <span class="sidebar-tags-form__tag-label-count">
                    ({{tag.topic_count}})
                  </span>
                </p>
              </label>
            </div>
          {{else}}
            <div class="sidebar-tags-form__no-tags">
              {{i18n "sidebar.tags_form_modal.no_tags"}}
            </div>
          {{/each}}
        </form>
      {{/if}}

      <DConditionalLoadingSpinner @condition={{this.tags.loadingMore}} />
    </EditNavigationMenuModal>
  </template>
}
